import React, { useState, useEffect } from 'react';
import { 
  Newspaper, 
  TrendingUp, 
  Scale, 
  Building2, 
  Users, 
  Clock,
  AlertCircle,
  ArrowUpRight,
  RefreshCw
} from 'lucide-react';
import { legalAPI } from '../services/api';
import LanguageSelector from './LanguageSelector';
import MarkdownRenderer from './MarkdownRenderer';
import toast from 'react-hot-toast';

const Overview = ({ user }) => {
  const [language, setLanguage] = useState(user?.preferredLanguage || 'en');
  const [loading, setLoading] = useState(true);
  const [newsLoading, setNewsLoading] = useState(false);
  const [news, setNews] = useState([]);
  const [legalData, setLegalData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    loadOverviewData();
  }, [language]);

  const loadOverviewData = async () => {
    try {
      setLoading(true);
      const [newsResponse, dataResponse] = await Promise.all([
        legalAPI.getNews({ language, limit: 5 }),
        legalAPI.getLegalData(language)
      ]);

      setNews(newsResponse.news || []);
      setLegalData(dataResponse.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to load overview data:', error);
      toast.error('Failed to load legal overview');
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = async (newLanguage) => {
    try {
      setLanguage(newLanguage);
      
      // Update user preference
      await legalAPI.updateLanguagePreference(newLanguage);
      toast.success('Language preference updated');
    } catch (error) {
      console.error('Failed to update language:', error);
      toast.error('Failed to update language preference');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return '#ef4444';
      case 'high': return '#f97316';
      case 'medium': return '#eab308';
      case 'low': return '#22c55e';
      default: return '#6b7280';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'supreme-court':
      case 'high-court':
        return <Scale size={16} />;
      case 'corporate-law':
        return <Building2 size={16} />;
      case 'constitutional-law':
        return <Users size={16} />;
      default:
        return <Newspaper size={16} />;
    }
  };

  if (loading) {
    return (
      <div className="overview-loading">
        <div className="loading-spinner">
          <RefreshCw size={24} className="animate-spin" />
          <p>Loading legal overview...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="overview-container">
      {/* Header */}
      <div className="overview-header">
        <div className="header-content">
          <div className="header-text">
            <h1>Legal Overview</h1>
            <p>Stay updated with the latest legal developments in India</p>
          </div>
          
          <div className="header-actions">
            <LanguageSelector 
              currentLanguage={language}
              onLanguageChange={handleLanguageChange}
              loading={newsLoading}
            />
            
            <button 
              className="refresh-btn"
              onClick={loadOverviewData}
              disabled={newsLoading}
            >
              <RefreshCw size={16} className={newsLoading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>
        
        {lastUpdated && (
          <div className="last-updated">
            <Clock size={14} />
            Last updated: {formatDate(lastUpdated)}
          </div>
        )}
      </div>

      {/* Latest Legal News - NOW AT THE TOP */}
      <div className="legal-news">
        <div className="news-header">
          <h2>Latest Legal News</h2>
        </div>
        
        {news.length > 0 ? (
          <div className="news-list">
            {news.map((article, index) => (
              <article key={article._id || index} className="news-card">
                <div className="news-header">
                  <div className="news-meta">
                    <div className="category-icon">
                      {getCategoryIcon(article.category)}
                    </div>
                    <span className="category">
                      {article.category.replace('-', ' ').toUpperCase()}
                    </span>
                    <div 
                      className="priority-indicator"
                      style={{ backgroundColor: getPriorityColor(article.priority) }}
                    />
                  </div>
                  <div className="news-date">
                    {formatDate(article.publishedAt)}
                  </div>
                </div>
                
                <h3 className="news-title">{article.title}</h3>
                
                {article.summary && (
                  <p className="news-summary">{article.summary}</p>
                )}
                
                <div className="news-footer">
                  <span className="news-source">{article.source}</span>
                  {article.sourceUrl && (
                    <a 
                      href={article.sourceUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="read-more"
                    >
                      Read more <ArrowUpRight size={14} />
                    </a>
                  )}
                </div>
                
                {article.tags && article.tags.length > 0 && (
                  <div className="news-tags">
                    {article.tags.slice(0, 3).map((tag, tagIndex) => (
                      <span key={tagIndex} className="tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>
        ) : (
          <div className="empty-news">
            <AlertCircle size={48} />
            <h3>Loading legal news...</h3>
            <p>Fetching the latest legal developments from India.</p>
          </div>
        )}
      </div>

      {/* Recent Judgments - NOW COMES AFTER LATEST NEWS */}
      {legalData?.recentJudgments && (
        <div className="recent-judgments">
          <h2>Recent Important Judgments</h2>
          <div className="judgments-grid">
            {legalData.recentJudgments.map((judgment, index) => (
              <div key={index} className="judgment-card">
                <div className="judgment-header">
                  <div className="court-name">{judgment.court}</div>
                  <div className={`impact-badge ${judgment.impact}`}>
                    {judgment.impact} impact
                  </div>
                </div>
                <h3>{judgment.title}</h3>
                <div className="judgment-meta">
                  <span className="date">{formatDate(judgment.date)}</span>
                  <span className="category">{judgment.category.replace('-', ' ')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Legal Statistics Dashboard */}
      {legalData && (
        <div className="legal-stats">
          <h2>Legal System Statistics</h2>
          
          <div className="stats-grid">
            {/* Supreme Court Stats */}
            <div className="stat-card supreme-court">
              <div className="stat-header">
                <Scale size={20} />
                <h3>Supreme Court</h3>
              </div>
              <div className="stat-numbers">
                <div className="stat-item">
                  <span className="number">{legalData.courtStatistics.supremeCourt.pendingCases.toLocaleString()}</span>
                  <span className="label">Pending Cases</span>
                </div>
                <div className="stat-item">
                  <span className="number">{legalData.courtStatistics.supremeCourt.newCases.toLocaleString()}</span>
                  <span className="label">New Cases</span>
                </div>
              </div>
            </div>

            {/* High Courts Stats */}
            <div className="stat-card high-court">
              <div className="stat-header">
                <Building2 size={20} />
                <h3>High Courts</h3>
              </div>
              <div className="stat-numbers">
                <div className="stat-item">
                  <span className="number">{legalData.courtStatistics.highCourts.pendingCases.toLocaleString()}</span>
                  <span className="label">Pending Cases</span>
                </div>
                <div className="stat-item">
                  <span className="number">{legalData.courtStatistics.highCourts.newCases.toLocaleString()}</span>
                  <span className="label">New Cases</span>
                </div>
              </div>
            </div>

            {/* Legislation Stats */}
            <div className="stat-card legislation">
              <div className="stat-header">
                <Users size={20} />
                <h3>Legislation</h3>
              </div>
              <div className="stat-numbers">
                <div className="stat-item">
                  <span className="number">{legalData.legislation.billsPending}</span>
                  <span className="label">Bills Pending</span>
                </div>
                <div className="stat-item">
                  <span className="number">{legalData.legislation.billsPassed}</span>
                  <span className="label">Bills Passed</span>
                </div>
              </div>
            </div>

            {/* Trends */}
            <div className="stat-card trends">
              <div className="stat-header">
                <TrendingUp size={20} />
                <h3>Litigation Trends</h3>
              </div>
              <div className="trends-list">
                {legalData.trends.mostLitigatedAreas.slice(0, 3).map((area, index) => (
                  <div key={index} className="trend-item">
                    <span className="trend-name">{area.area}</span>
                    <span className="trend-percentage">{area.percentage}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .overview-container {
          padding: 40px;
          max-width: 1600px;
          margin: 0 auto;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          min-height: 100vh;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        }

        .overview-header {
          background: white;
          border-radius: 20px;
          padding: 32px;
          margin-bottom: 40px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(10px);
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 20px;
        }

        .header-text h1 {
          font-size: 36px;
          font-weight: 800;
          background: linear-gradient(135deg, #1f2937 0%, #4f46e5 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin: 0 0 12px 0;
          letter-spacing: -0.02em;
        }

        .header-text p {
          color: #6b7280;
          font-size: 18px;
          margin: 0;
          font-weight: 400;
          opacity: 0.8;
        }

        .header-actions {
          display: flex;
          gap: 16px;
          align-items: center;
        }

        .refresh-btn {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 20px;
          border: 2px solid transparent;
          border-radius: 12px;
          background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
          color: white;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
        }

        .refresh-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(79, 70, 229, 0.4);
          background: linear-gradient(135deg, #3730a3 0%, #6d28d9 100%);
        }

        .refresh-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
          box-shadow: 0 4px 12px rgba(79, 70, 229, 0.2);
        }

        .last-updated {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #6b7280;
          font-size: 14px;
          padding: 12px 16px;
          background: #f8fafc;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
        }

        .legal-stats {
          margin-bottom: 40px;
        }

        .legal-stats h2 {
          font-size: 24px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 20px;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 20px;
        }

        .stat-card {
          background: white;
          border-radius: 16px;
          padding: 28px;
          border-left: 5px solid #3b82f6;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          width: 100px;
          height: 100px;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%);
          border-radius: 0 0 0 100px;
          pointer-events: none;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.05);
        }

        .stat-card.supreme-court {
          border-left-color: #dc2626;
        }

        .stat-card.high-court {
          border-left-color: #ea580c;
        }

        .stat-card.legislation {
          border-left-color: #7c3aed;
        }

        .stat-card.trends {
          border-left-color: #059669;
        }

        .stat-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }

        .stat-header h3 {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
        }

        .stat-numbers {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-width: 0;
          overflow: hidden;
        }

        .stat-item .number {
          font-size: 20px;
          font-weight: 700;
          color: #1f2937;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .stat-item .label {
          font-size: 12px;
          color: #6b7280;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .trends-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .trend-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 14px;
        }

        .trend-name {
          color: #374151;
        }

        .trend-percentage {
          font-weight: 600;
          color: #059669;
        }

        .recent-judgments {
          margin-bottom: 40px;
        }

        .recent-judgments h2 {
          font-size: 24px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 20px;
        }

        .judgments-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 20px;
        }

        .judgment-card {
          background: white;
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .judgment-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #3b82f6, #8b5cf6);
        }

        .judgment-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.05);
        }

        .judgment-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .court-name {
          font-size: 12px;
          color: #6b7280;
          text-transform: uppercase;
          font-weight: 500;
        }

        .impact-badge {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 500;
          text-transform: uppercase;
        }

        .impact-badge.high {
          background: #fef2f2;
          color: #dc2626;
        }

        .impact-badge.medium {
          background: #fefbf2;
          color: #d97706;
        }

        .judgment-card h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 12px 0;
          line-height: 1.4;
        }

        .judgment-meta {
          display: flex;
          gap: 16px;
          font-size: 14px;
          color: #6b7280;
        }

        .legal-news {
          margin-bottom: 80px;
        }

        .legal-news h2 {
          font-size: 24px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 20px;
        }

        .news-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .seed-news-btn {
          padding: 8px 16px;
          border: 1px solid #3b82f6;
          border-radius: 6px;
          background: #3b82f6;
          color: white;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .seed-news-btn:hover {
          background: #2563eb;
        }

        .news-list {
          display: flex;
          flex-direction: column;
          gap: 20px;
          max-width: 800px;
        }

        .news-card {
          background: white;
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .news-card::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 4px;
          height: 100%;
          background: linear-gradient(180deg, #3b82f6, #10b981);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .news-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1), 0 6px 20px rgba(0, 0, 0, 0.05);
        }

        .news-card:hover::after {
          opacity: 1;
        }

        .news-card .news-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .news-meta {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .category-icon {
          color: #6b7280;
        }

        .category {
          font-size: 12px;
          color: #6b7280;
          font-weight: 500;
        }

        .priority-indicator {
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }

        .news-date {
          font-size: 12px;
          color: #6b7280;
        }

        .news-title {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 12px 0;
          line-height: 1.4;
        }

        .news-summary {
          font-size: 14px;
          color: #4b5563;
          line-height: 1.5;
          margin: 0 0 16px 0;
        }

        .news-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .news-source {
          font-size: 12px;
          color: #6b7280;
          font-weight: 500;
        }

        .read-more {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: #3b82f6;
          text-decoration: none;
          font-weight: 500;
        }

        .read-more:hover {
          color: #2563eb;
        }

        .news-tags {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .tag {
          padding: 4px 8px;
          background: #f1f5f9;
          color: #475569;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 500;
        }

        .empty-news {
          text-align: center;
          padding: 60px 20px;
          color: #6b7280;
        }

        .empty-news h3 {
          margin: 16px 0 8px 0;
          color: #374151;
        }

        .overview-loading {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 400px;
        }

        .loading-spinner {
          text-align: center;
          color: #6b7280;
        }

        .loading-spinner p {
          margin-top: 12px;
          font-size: 14px;
        }

        @media (max-width: 768px) {
          .overview-container {
            padding: 16px;
          }

          .header-content {
            flex-direction: column;
            gap: 16px;
            align-items: flex-start;
          }

          .header-actions {
            width: 100%;
            justify-content: space-between;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .judgments-grid {
            grid-template-columns: 1fr;
          }

          .news-list {
            max-width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default Overview;
